import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, logout } from '../utils/api';
import toast from 'react-hot-toast';
import { FaCheck, FaCrown, FaStar, FaArrowLeft, FaEye } from 'react-icons/fa';

const PricingPage = () => {
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load user data and subscription plans in parallel for better performance
      const [userData, plansResponse] = await Promise.allSettled([
        getUser(),
        fetch('/api/subscriptions/plans', {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        })
      ]);

      // Handle user data
      if (userData.status === 'fulfilled') {
        setUser(userData.value);
      } else {
        console.error('Error fetching user data:', userData.reason);
        // Don't show error for user data, as it's not critical for pricing page
      }

      // Handle plans data
      if (plansResponse.status === 'fulfilled') {
        const plansData = await plansResponse.value.json();
        setPlans(plansData.plans || []);
      } else {
        console.error('Error fetching plans data:', plansResponse.reason);
        toast.error('Failed to load subscription plans');
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load pricing information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!user) {
      toast.error('Please log in to subscribe');
      navigate('/admin/login');
      return;
    }

    setSelectedPlan(plan);
    setProcessing(true);

    try {
      const response = await fetch('/api/subscriptions/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ planId: plan.id }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create subscription');
      }

      // Initialize Razorpay payment
      const options = {
        key: data.razorpayOrder.key,
        amount: data.razorpayOrder.amount,
        currency: data.razorpayOrder.currency,
        order_id: data.razorpayOrder.id,
        name: 'Ekdant Arts',
        description: `${plan.name} Subscription`,
        handler: async function (response) {
          // Handle successful payment
          await verifyPayment(response, data.subscription.id);
        },
        prefill: {
          name: user.name,
          email: user.email,
        },
        theme: {
          color: '#3B82F6',
        },
        modal: {
          ondismiss: function() {
            setProcessing(false);
            setSelectedPlan(null);
            toast.error('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.message || 'Failed to start subscription process');
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const verifyPayment = async (razorpayResponse, subscriptionId) => {
    try {
      const response = await fetch('/api/subscriptions/verify-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
          subscriptionId,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Subscription activated successfully!');
        // Redirect to dashboard after successful subscription
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 2000);
      } else {
        throw new Error(data.error || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error(error.message || 'Payment verification failed');
    } finally {
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/admin/login');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to logout');
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!contactForm.name.trim() || !contactForm.phone.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate phone number
    if (!/^[6-9]\d{9}$/.test(contactForm.phone.trim())) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Store inquiry in localStorage for demo purposes
    const inquiry = {
      id: `pricing-inquiry-${Date.now()}`,
      ...contactForm,
      timestamp: new Date().toISOString(),
      status: 'received',
      type: 'pricing'
    };

    const existingInquiries = JSON.parse(localStorage.getItem('demoInquiries') || '[]');
    existingInquiries.push(inquiry);
    localStorage.setItem('demoInquiries', JSON.stringify(existingInquiries));

    // Reset form
    setContactForm({
      name: '',
      phone: '',
      email: '',
      message: ''
    });

    toast.success('Thank you for your message! We\'ll get back to you soon.', {
      duration: 5000
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/demo')}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors px-3 py-2 rounded-lg hover:bg-gray-100"
                title="Back to Demo"
              >
                <FaArrowLeft className="mr-2" />
                <FaEye className="mr-2" />
                <span className="hidden sm:inline">Back to Demo</span>
                <span className="sm:hidden">Demo</span>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Choose Your Plan</h1>
            </div>

            {user && (
              <div className="flex items-center space-x-4">
                <span className="text-gray-700">Welcome, {user.name}</span>
                <button
                  onClick={handleLogout}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!user && (
          <div className="text-center mb-8">
            <h2 className="text-xl text-gray-600 mb-4">
              Please log in to subscribe to a plan
            </h2>
            <button
              onClick={() => navigate('/admin/login')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Log In to Continue
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-xl shadow-lg border-2 transition-all duration-300 ${
                plan.id === 'pro'
                  ? 'border-indigo-500 shadow-indigo-100'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Plan Header */}
              <div className={`p-6 rounded-t-xl ${
                plan.id === 'pro'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                  : 'bg-gray-50'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">{plan.name}</h3>
                    <div className="mt-2">
                      <span className={`text-3xl font-bold ${
                        plan.id === 'pro' ? 'text-white' : 'text-gray-900'
                      }`}>
                        ₹{plan.priceInRupees?.toLocaleString('en-IN')}
                      </span>
                      <span className={`text-lg ml-1 ${
                        plan.id === 'pro' ? 'text-indigo-100' : 'text-gray-600'
                      }`}>
                        /year
                      </span>
                    </div>
                  </div>
                  {plan.id === 'pro' && (
                    <div className="flex items-center">
                      <FaCrown className="text-yellow-300 text-2xl mr-2" />
                      <FaStar className="text-yellow-300 text-xl" />
                    </div>
                  )}
                </div>
              </div>

              {/* Plan Features */}
              <div className="p-6">
                <ul className="space-y-3">
                  {plan.features.inventory && (
                    <li className="flex items-center">
                      <FaCheck className="text-green-500 mr-3 flex-shrink-0" />
                      <span>Inventory Management</span>
                    </li>
                  )}
                  {plan.features.retailOrders && (
                    <li className="flex items-center">
                      <FaCheck className="text-green-500 mr-3 flex-shrink-0" />
                      <span>Retail Order Creation</span>
                    </li>
                  )}
                  {plan.features.wholesaleOrders && (
                    <li className="flex items-center">
                      <FaCheck className="text-green-500 mr-3 flex-shrink-0" />
                      <span>Wholesale Order Creation</span>
                    </li>
                  )}
                  {plan.features.finance && (
                    <li className="flex items-center">
                      <FaCheck className="text-green-500 mr-3 flex-shrink-0" />
                      <span>Finance Dashboard & Analytics</span>
                    </li>
                  )}
                  {plan.features.admin && (
                    <li className="flex items-center">
                      <FaCheck className="text-green-500 mr-3 flex-shrink-0" />
                      <span>Admin Order Management</span>
                    </li>
                  )}
                  {plan.features.gallery && (
                    <li className="flex items-center">
                      <FaCheck className="text-green-500 mr-3 flex-shrink-0" />
                      <span>Inventory Gallery View</span>
                    </li>
                  )}
                  {plan.features.loginHistory && (
                    <li className="flex items-center">
                      <FaCheck className="text-green-500 mr-3 flex-shrink-0" />
                      <span>Login History Tracking</span>
                    </li>
                  )}
                </ul>

                {/* Subscribe Button */}
                <div className="mt-8">
                  {user ? (
                    <button
                      onClick={() => handleSubscribe(plan)}
                      disabled={processing && selectedPlan?.id === plan.id}
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                        plan.id === 'pro'
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
                          : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {processing && selectedPlan?.id === plan.id ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Processing...
                        </div>
                      ) : (
                        `Subscribe to ${plan.name}`
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/admin/login')}
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                        plan.id === 'pro'
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Log In to Subscribe
                    </button>
                  )}
                </div>

                {plan.id === 'pro' && (
                  <div className="mt-4 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                      <FaCrown className="mr-1" />
                      Most Popular
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Text */}
        <div className="text-center mt-12 text-gray-600">
          <p className="text-sm">
            All plans include secure payment processing and 24/7 support.
            <br />
            Cancel anytime with no hidden fees.
          </p>
        </div>

        {/* Contact Us Section */}
        <div className="mt-16">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-8 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">📬 Questions? Get in Touch</h3>
            <p className="text-emerald-100 mb-6 max-w-3xl mx-auto">
              Have questions about our subscription plans or need help getting started?
              We're here to help you choose the right plan for your business needs.
            </p>

            <div className="bg-white rounded-lg p-8 shadow-lg max-w-2xl mx-auto">
              <h4 className="text-xl font-semibold text-gray-900 mb-6">Send us your message</h4>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={contactForm.phone}
                      onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="9876543210"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Message *
                  </label>
                  <textarea
                    rows="4"
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Please describe your questions or requirements..."
                  />
                </div>

                {/* Demo Notice */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    🎭 <strong>Demo Mode:</strong> This is a demonstration contact form. Your message will be stored locally and we'll show a confirmation message.
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center">
                  <button
                    type="submit"
                    className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
                  >
                    📤 Send Message
                  </button>
                </div>
              </form>

              {/* Contact Information */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h5 className="text-lg font-semibold text-gray-900 mb-4">📞 Alternative Contact Methods</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <strong>📧 Email:</strong><br />
                    support@ekdantarts.com
                  </div>
                  <div>
                    <strong>📱 Phone:</strong><br />
                    Available during business hours
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <strong>⏰ Response Time:</strong> We typically respond within 24 hours
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Razorpay Script */}
      <script src="https://checkout.razorpay.com/v1/checkout.js"></script>
    </div>
  );
};

export default PricingPage;
